import {
  BotSequenceTestRunInterface,
  BotSequenceTestRunTrace,
  BullCancelJobsBody,
  BullJobType,
  BullQueueJobBody,
  BullQueueJobsBody,
  BullQueueJobsResult,
  BullQueueName,
  BullQueueParentFlowBody,
  BullSyncSingleEntityBody,
  CreateTestRunBody,
  CreateTestRunResult,
  FMSLocationData,
  FMSSyncedUserData,
  ProcessSequenceUserEventsJobData
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

//LINK - apps/bull/src/API/Bull/Bull.Controller.ts
export class BullAPI {
  static async queueJobs(body: BullQueueJobsBody, inBulk?: boolean) {
    const params = inBulk !== undefined ? { inBulk } : {};
    return APIService.Bull.post<BullQueueJobsResult>("/bull/queue-jobs", body, params);
  }

  static async queueJob(body: BullQueueJobBody) {
    return APIService.Bull.post("/bull/queue-job", body);
  }

  static async queueParentChildJobs(body: BullQueueParentFlowBody) {
    return APIService.Bull.post("/bull/queue-parent-child-jobs", body);
  }

  static async removeJobs(body: BullCancelJobsBody) {
    return APIService.Bull.post("/bull/remove-jobs", body);
  }

  // static async syncUser(userId: number, body: { location: FMSLocationData }) {
  //   return APIService.Bull.post<FMSSyncedUserData>(`/bull/sync/user/${userId}`, body);
  // }
  static async syncUsersForLocation(orgId: number, locationId: number, userIds: number[]) {
    return APIService.Bull.put(`/bull/sync/orgId/${orgId}/location/${locationId}/users`, { userIds });
  }

  static async syncSingleEntity(body: BullSyncSingleEntityBody): Promise<boolean> {
    return APIService.Bull.post("/bull/sync-single-entity", body);
  }

  static async markSequenceUserEventAsSuccess(userId: number, sequenceUserEventId: number): Promise<boolean> {
    return APIService.Bull.post(`/bull/user/${userId}/sequence-event/${sequenceUserEventId}/success`);
  }

  // ── Sequence full-flow test runs (non-prod; docs/specs/new-sequence-approach/full-flow-test-run.md) ──
  // LINK apps/bull/src/API/BotSequences/BotSequences.Controller.ts

  static async createSequenceTestRun(orgId: number, body: CreateTestRunBody): Promise<CreateTestRunResult> {
    return APIService.Bull.post(`/bot-sequences/org/${orgId}/test-run`, body);
  }

  static async listActiveSequenceTestRuns(orgId: number): Promise<BotSequenceTestRunInterface[]> {
    return APIService.Bull.get(`/bot-sequences/org/${orgId}/test-runs`);
  }

  static async getSequenceTestRunTrace(runId: number): Promise<BotSequenceTestRunTrace> {
    return APIService.Bull.get(`/bot-sequences/test-run/${runId}`);
  }

  static async cancelSequenceTestRun(runId: number): Promise<BotSequenceTestRunInterface> {
    return APIService.Bull.post(`/bot-sequences/test-run/${runId}/cancel`);
  }

  /** Closes an open enrollment so the next reconcile opens a fresh episode (non-prod only). */
  static async manualUnenrollSequenceEnrollment(
    orgId: number,
    enrollmentId: number
  ): Promise<{ closed: boolean; reconcileRequestId: number | null }> {
    return APIService.Bull.post(`/bot-sequences/org/${orgId}/enrollment/${enrollmentId}/manual-unenroll`);
  }

  /** Forces one out-of-cadence dispatch tick; the tick's own lock + enable gate still apply. */
  static async forceSequenceDispatchTick(): Promise<{ enqueued: boolean }> {
    return APIService.Bull.post("/bot-sequences/dispatch-tick");
  }

  /** Claims + enqueues today's morning run; `runId: null` = today's run already exists. */
  static async forceSequenceSchedulerRun(orgId: number): Promise<{ runId: number | null }> {
    return APIService.Bull.post(`/bot-sequences/org/${orgId}/scheduler-run`);
  }

  static async calculateCrmUsersData(users: { orgId: number; userId: number }[]): Promise<void> {
    await BullAPI.queueJobs(
      [
        {
          queueName: BullQueueName.CRM,
          jobs: users.map(({ orgId, userId }) => ({
            name: BullJobType.CRMCalculateUserData,
            data: { orgId, userId },
            options: {
              removeOnComplete: { age: 60 * 60 * 2, count: 1000 },
              removeOnFail: { age: 60 * 60 * 24, count: 500 }
            }
          }))
        }
      ],
      true
    );
  }
}
